/**
 * @zendir/ui - ImageGallery Component
 *
 * Grid of image thumbnails with lightbox, download, delete, and pagination.
 * Designed for spacecraft image capture galleries in operator dashboards.
 *
 * Astro UX Compliance:
 * - Consistent card styling with GlassCard
 * - Status-based border colors
 * - Keyboard navigation (Escape to close lightbox)
 * - Focus management
 *
 * @example
 * ```tsx
 * <ImageGallery
 *   images={[
 *     { id: '1', name: 'capture_001.png', src: dataUrl, timestamp: Date.now() },
 *     { id: '2', name: 'capture_002.png', src: dataUrl, timestamp: Date.now() },
 *   ]}
 *   columns={[2, 3, 4]}
 *   onDownload={(img) => saveAs(img.src, img.name)}
 *   onDelete={(img) => removeImage(img.id)}
 *   pageSize={12}
 * />
 * ```
 */

import React, { memo, useState, useCallback, useEffect } from "react";
import { useTheme } from "../../theme";
import { useBreakpoint } from "../layout/useBreakpoint";
import { resolveResponsiveValue } from "../layout/useBreakpoint";
import type { ResponsiveValue } from "../layout/useBreakpoint";
import { Pagination } from "../navigation/Pagination";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GalleryImage {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Image source URL or data URL */
  src: string;
  /** Timestamp (ms since epoch) */
  timestamp?: number;
  /** Optional short label shown as a pill over the thumbnail (e.g. "NEW"). */
  badge?: string;
  /** Optional metadata */
  metadata?: Record<string, unknown>;
}

export interface ImageGalleryProps {
  /** Array of images */
  images: GalleryImage[];
  /** Number of columns (responsive) */
  columns?: ResponsiveValue<number>;
  /** Images per page (0 = no pagination) */
  pageSize?: number;
  /** Download handler */
  onDownload?: (image: GalleryImage) => void;
  /** Delete handler (shows delete button when provided) */
  onDelete?: (image: GalleryImage) => void;
  /** Click handler */
  onClick?: (image: GalleryImage) => void;
  /** Show lightbox on click (default true) */
  lightbox?: boolean;
  /** Thumbnail height */
  thumbnailHeight?: number;
  /** Empty state message */
  emptyMessage?: string;
  /** New image badge count (shows badge on component) */
  newCount?: number;
  /** Custom style */
  style?: React.CSSProperties;
}

export const ImageGallery = memo(function ImageGallery({
  images,
  columns = [2, 3, 4],
  pageSize = 12,
  onDownload,
  onDelete,
  onClick,
  lightbox = true,
  thumbnailHeight = 140,
  emptyMessage = "No images captured",
  newCount: _newCount,
  style,
}: ImageGalleryProps): React.ReactElement {
  const { tokens } = useTheme();
  const { breakpoint } = useBreakpoint();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const cols = Array.isArray(columns)
    ? resolveResponsiveValue(columns, breakpoint)
    : columns;
  const totalPages = pageSize > 0 ? Math.ceil(images.length / pageSize) : 1;
  const pageImages =
    pageSize > 0
      ? images.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
      : images;

  // Reset page if images change
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [images.length, totalPages, currentPage]);

  // Lightbox keyboard
  useEffect(() => {
    if (!selectedImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedImage(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedImage]);

  const handleImageClick = useCallback(
    (img: GalleryImage) => {
      onClick?.(img);
      if (lightbox) setSelectedImage(img);
    },
    [onClick, lightbox],
  );

  const formatTime = (ts?: number) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Action button helper
  const ActionBtn = ({
    icon,
    label,
    onClick: handler,
    color,
  }: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    color?: string;
  }) => (
    <button
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        handler();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        border: `1px solid ${tokens.colors.border.muted}`,
        borderRadius: tokens.borderRadius.sm,
        backgroundColor: `${tokens.colors.background.elevated}e0`,
        color: color || tokens.colors.text.secondary,
        cursor: "pointer",
        transition: tokens.animation.fast,
        backdropFilter: "blur(4px)",
      }}
    >
      {icon}
    </button>
  );

  // Download icon
  const downloadIcon = (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
  // Delete icon
  const deleteIcon = (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );

  return (
    <div style={{ fontFamily: tokens.typography.fontFamily.primary, ...style }}>
      {/* Empty state */}
      {images.length === 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
            color: tokens.colors.text.tertiary,
            fontSize: tokens.typography.fontSize.sm,
            border: `1px dashed ${tokens.colors.border.muted}`,
            borderRadius: tokens.borderRadius.md,
          }}
        >
          {emptyMessage}
        </div>
      )}

      {/* Grid */}
      {pageImages.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: tokens.spacing.sm,
          }}
        >
          {pageImages.map((img) => {
            const isHovered = hoveredId === img.id;
            return (
              <div
                key={img.id}
                role="button"
                tabIndex={0}
                aria-label={`View image ${img.name || img.id}`}
                onClick={() => handleImageClick(img)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleImageClick(img);
                  }
                }}
                onMouseEnter={() => setHoveredId(img.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  position: "relative",
                  borderRadius: tokens.borderRadius.md,
                  border: `1px solid ${tokens.colors.border.muted}`,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: tokens.animation.fast,
                  transform: isHovered ? "translateY(-2px)" : "none",
                  boxShadow: isHovered ? tokens.shadows.md : "none",
                  backgroundColor: tokens.colors.background.surface,
                }}
              >
                {/* Per-image badge (e.g. newest capture) — overlays the thumbnail */}
                {img.badge ? (
                  <span
                    style={{
                      position: "absolute",
                      top: tokens.spacing.xs,
                      left: tokens.spacing.xs,
                      zIndex: 1,
                      padding: `2px ${tokens.spacing.xs}`,
                      borderRadius: tokens.borderRadius.sm,
                      backgroundColor: tokens.colors.accent.primary,
                      color: "#fff",
                      fontSize: tokens.typography.fontSize.micro,
                      fontWeight: tokens.typography.fontWeight.bold,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      lineHeight: 1.4,
                      pointerEvents: "none",
                      boxShadow: tokens.shadows.sm,
                    }}
                  >
                    {img.badge}
                  </span>
                ) : null}

                {/* Thumbnail */}
                <div
                  style={{
                    height: thumbnailHeight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundColor: tokens.colors.background.base,
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.name}
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.parentElement!.innerHTML = `<span style="color:${tokens.colors.text.tertiary};font-size:${tokens.typography.fontSize.xxs}">Image unavailable</span>`;
                    }}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>

                {/* Info bar */}
                <div
                  style={{
                    padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                    borderTop: `1px solid ${tokens.colors.border.muted}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: tokens.typography.fontSize.xxs,
                      fontWeight: tokens.typography.fontWeight.medium,
                      color: tokens.colors.text.primary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {img.name}
                  </div>
                  {img.timestamp && (
                    <div
                      style={{
                        fontSize: tokens.typography.fontSize.micro,
                        color: tokens.colors.text.tertiary,
                        fontFamily: tokens.typography.fontFamily.mono,
                      }}
                    >
                      {formatTime(img.timestamp)}
                    </div>
                  )}
                </div>

                {/* Hover actions */}
                {isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      top: tokens.spacing.xs,
                      right: tokens.spacing.xs,
                      display: "flex",
                      gap: "4px",
                    }}
                  >
                    {onDownload && (
                      <ActionBtn
                        icon={downloadIcon}
                        label="Download"
                        onClick={() => onDownload(img)}
                      />
                    )}
                    {onDelete && (
                      <ActionBtn
                        icon={deleteIcon}
                        label="Delete"
                        onClick={() => onDelete(img)}
                        color={tokens.colors.status.critical}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: tokens.spacing.md,
          }}
        >
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            onChange={(page) => setCurrentPage(Math.max(0, page - 1))}
            showFirstLast={false}
            displayMode="text"
            size="small"
          />
        </div>
      )}

      {/* Lightbox */}
      {selectedImage && (
        <>
          <div
            aria-hidden="true"
            onClick={() => setSelectedImage(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              backgroundColor: `${tokens.colors.background.base}e0`,
              backdropFilter: "blur(8px)",
              animation: `zendir-lb-fade 200ms ${tokens.animation.easing.default}`,
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Image: ${selectedImage.name}`}
            onClick={() => setSelectedImage(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: tokens.spacing.xl,
            }}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.name}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: tokens.borderRadius.md,
                boxShadow: tokens.shadows.xl,
                animation: `zendir-lb-scale 250ms ${tokens.animation.easing.default}`,
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.spacing.md,
                marginTop: tokens.spacing.md,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <span
                style={{
                  color: tokens.colors.text.primary,
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.medium,
                }}
              >
                {selectedImage.name}
              </span>
              {onDownload && (
                <button
                  onClick={() => onDownload(selectedImage)}
                  style={{
                    padding: "6px 16px",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.primary,
                    backgroundColor: `${tokens.colors.accent.primary}20`,
                    border: `1px solid ${tokens.colors.accent.primary}40`,
                    borderRadius: tokens.borderRadius.md,
                    cursor: "pointer",
                    fontFamily: tokens.typography.fontFamily.primary,
                  }}
                >
                  Download
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => {
                    onDelete(selectedImage);
                    setSelectedImage(null);
                  }}
                  style={{
                    padding: "6px 16px",
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.status.critical,
                    backgroundColor: `${tokens.colors.status.critical}15`,
                    border: `1px solid ${tokens.colors.status.critical}30`,
                    borderRadius: tokens.borderRadius.md,
                    cursor: "pointer",
                    fontFamily: tokens.typography.fontFamily.primary,
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
          <style>{`
            @keyframes zendir-lb-fade { from { opacity: 0; } to { opacity: 1; } }
            @keyframes zendir-lb-scale { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
          `}</style>
        </>
      )}
    </div>
  );
});

export default ImageGallery;
